import React, { useId, useState } from 'react';
import { ActionIcon, Flex, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconPhoto, IconPhotoEdit, IconX } from '@tabler/icons-react';
import Image from 'next/image';
import { FieldProps } from '../form-helpers';
import ImagePreviewModal from '../ImagePreviewModal';
import styles from './FormFieldImage.module.css';

export type FormFieldImageProps = Omit<FieldProps, 'value' | 'onChange'> & {
    value: string | null;
    onChange: (attachmentID: string | null) => void;
    onUpload?: (file: File) => Promise<string>;
    resolveImageSrc: (attachmentID: string) => string;
};

function FormFieldImage({ label, required, value, onChange, onUpload, resolveImageSrc, error, disabled }: FormFieldImageProps) {
    const inputId = useId();
    const [uploading, setUploading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const uploadFile = async (file: File) => {
        if (!file || !onUpload) return;
        setUploading(true);
        try {
            const attachmentID = await onUpload(file);
            onChange(attachmentID);
        } catch {
            // `onUpload` rejects when the file is not an image, exceeds the tenant's upload-size
            // limit, or the attachment POST fails — it has already surfaced a toast in each case.
            // Swallowing here keeps the existing value intact (a failed *replace* must not clear
            // the current image) and avoids an unhandled rejection from this event handler.
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
    };

    const handleDrop = (files: File[]) => uploadFile(files[0]);

    return (
        <div>
            {label && <Text size="sm" fw={500} mt="sm">{label} {required ? <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span> : null}</Text>}

            {value ? (
                <div className={styles.thumbWrap}>
                    <div
                        className={styles.thumb}
                        role="button"
                        tabIndex={0}
                        aria-label="View full-size image"
                        data-testid="form-field-image-thumbnail"
                        onClick={() => setPreviewOpen(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPreviewOpen(true); }}
                    >
                        <Image src={resolveImageSrc(value)} alt="" fill style={{ objectFit: 'contain' }} />
                        {!disabled && (
                            <div className={styles.thumbOverlayActions} onClick={(e) => e.stopPropagation()}>
                                <ActionIcon component="label" htmlFor={inputId} variant="filled" color="dark" size="sm" aria-label="Replace image">
                                    <IconPhotoEdit size={14} />
                                </ActionIcon>
                                <ActionIcon variant="filled" color="red" size="sm" aria-label="Remove image" onClick={() => onChange(null)}>
                                    <IconX size={14} />
                                </ActionIcon>
                                <input id={inputId} data-testid="form-field-image-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
                            </div>
                        )}
                    </div>
                    {previewOpen && <ImagePreviewModal src={resolveImageSrc(value)} onClose={() => setPreviewOpen(false)} />}
                </div>
            ) : (
                <div style={{ marginTop: '0.5rem' }}>
                    <Dropzone
                        onDrop={handleDrop}
                        accept={['image/*']}
                        multiple={false}
                        loading={uploading}
                        loaderProps={{ size: 20 }}
                        disabled={disabled}
                        radius="sm"
                        w={120}
                        h={120}
                        p={8}
                        className={styles.dropzone}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        inputProps={{ 'data-testid': 'form-field-image-input' } as React.InputHTMLAttributes<HTMLInputElement>}
                    >
                        <Flex direction="column" align="center" justify="center" gap={4} style={{ pointerEvents: 'none' }}>
                            <IconPhoto size={20} color="var(--mantine-color-scBlue-6)" stroke={1.6} />
                            <Text size="10px" lh={1.2} c="scBlue.6" fw={600} ta="center">Click or drag image</Text>
                        </Flex>
                    </Dropzone>
                    {error && <Text size="xs" c="yellow.6" mt={4}>{error}</Text>}
                </div>
            )}
        </div>
    );
}

export default React.memo(FormFieldImage);
